<template>
  <DragDialog
    :title="`上传列表(${fileList.length})`"
    :width="1200"
    :visible.sync="visible"
    :footerBtn="[]"
    :cancel="closeDialog"
  >
    <div class="upload-table-box">
      <div v-if="isLoading" class="loading-box">
        <Spin
          :pointNum="8"
          :translateX="25"
          background="var(--base-primary-bgColor)"
        />
      </div>
      <div class="clear-btn-box">
        <div class="common-button-box">
          <el-button
            type="primary"
            class="primary-btn"
            size="small"
            @click="clearWaitngFiles"
          >
            一键清理
          </el-button>
        </div>
      </div>
      <div v-if="!isLoading" class="common-table-box">
        <el-table
          height="100%"
          highlight-selection-row
          class="publish-table"
          :data="fileList"
        >
          <el-table-column label="序号" type="index" width="75" />
          <el-table-column label="文件名称" width="320">
            <template v-slot="{ row }">
              <span class="text-overflow" :title="row.fileName">
                {{ row.fileName }}
              </span>
            </template>
          </el-table-column>
          <el-table-column label="文件大小" width="120">
            <template v-slot="{ row }">
              <span>
                {{
                  `${formatFileSize(row.length).fileSize}
                ${formatFileSize(row.length).unit}`
                }}
              </span>
            </template>
          </el-table-column>
          <el-table-column label="上传进度" width="270">
            <template v-slot="{ row }">
              <div v-if="row.status === -1 || row.status === -2">等待上传</div>
              <div v-else-if="row.status === -3">等待更新</div>
              <ProgressBar
                v-else
                mark="dialog-"
                :fileInfo="row"
                :uploadControl="uploadControl"
              />
            </template>
          </el-table-column>
          <el-table-column label="上传至">
            <template v-slot="{ row }">
              <span class="text-overflow" :title="row.uploadPathName">
                {{ row.uploadPathName }}
              </span>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="80">
            <template v-slot="{ row }">
              <div class="common-button-box operation-cell">
                <el-button
                  type="text"
                  size="small"
                  :disabled="row.status === 1"
                  @click="deleteFileById(row)"
                >
                  删除
                </el-button>
              </div>
            </template>
          </el-table-column>
          <template slot="empty">
            <div class="common-empty-box">
              <bj-empty
                :image="require('@/assets/img/no-data.png')"
                class="empty-page"
              >
                <template slot="description">
                  <div class="empty-text">抱歉，没有找到相关内容</div>
                </template>
              </bj-empty>
            </div>
          </template>
        </el-table>
      </div>
    </div>
  </DragDialog>
</template>

<script>
import DragDialog from "@/components/CustomDragDialog.vue";
import Spin from "@/components/Spin.vue";
import ProgressBar from "@/components/ProgressBar.vue";

import { Store } from "@/utils/store";
import CustomUploader from "@/utils/customUploader";

import { formatFileSize, formatTime, getTokenId } from "@/utils/generalMethods";

import { getFileList, deleteFileService } from "@/service/interface";

let mimeTypes = "";
if (window.isLinux) {
  mimeTypes = ".pak,.ifc,.rvt,.obj,.obj.zip,.stl";
} else {
  // prettier-ignore
  mimeTypes = ".pak,.rvt,.3dxml,.gim,.ifc,.igms,.fbx,.3dm,.stp,.gltf,.skp,.glb,.dgn,.nwd,.nwc,.rvm,.obj,.obj.zip,.stl";
}

const UPLOADER_MAP = [
  {
    schemeType: "Bim",
    accept: {
      title: "模型文件",
      extensions: "*",
      mimeTypes,
    },
  },
  {
    schemeType: "Rs",
    accept: {
      title: "遥感文件",
      extensions: "*",
      mimeTypes: ".doezip",
    },
  },
  {
    schemeType: "Osgb",
    accept: {
      title: "倾斜摄影",
      extensions: "*",
      mimeTypes: ".osgbzip",
    },
  },
  {
    schemeType: "Panorama",
    accept: {
      title: "360全景图",
      extensions: "zip,rar,7z",
      mimeTypes: "application/zip",
    },
  },
  {
    schemeType: "PointCloud",
    accept: {
      title: "点云",
      extensions: "zip,rar,7z",
      mimeTypes: "application/zip",
    },
  },
  {
    schemeType: "Cad",
    accept: {
      title: "二维图纸文件",
      extensions: "*",
      mimeTypes: ".dwg,.dxf,.pdf",
    },
  },
  {
    schemeType: "Vector",
    accept: {
      title: "矢量数据文件",
      extensions: "*",
      mimeTypes: ".shp.zip,.kml,.kmz",
    },
  },
];

export default {
  data() {
    return {
      isLoading: false,
      fileList: [],
      timer: null,
    };
  },
  props: {
    visible: Boolean,
    closeDialog: Function,
  },
  components: {
    DragDialog,
    Spin,
    ProgressBar,
  },
  beforeDestroy() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }

    this.$eventManage.off("startUploadFile", this.initUploadList);
  },
  watch: {
    visible: function (newVal) {
      if (newVal) {
        this.initUploadList();

        this.$eventManage.on("startUploadFile", this.initUploadList);
      } else {
        clearInterval(this.timer);
        this.timer = null;

        this.$eventManage.off("startUploadFile", this.initUploadList);
      }
    },
    fileList: function (newVal, oldVal) {
      if (newVal.length !== oldVal.length) {
        this.$eventManage.fire("refreshTable");
      }
    },
  },
  methods: {
    clearWaitngFiles() {
      let waitingFiles = this.fileList.filter(
        (el) => el.status === -1 || el.status === -2
      );
      if (!waitingFiles.length) {
        this.$message.warning("当前没有等待上传的文件，无需清理");
        return;
      }

      let warningMsg = "";
      if (waitingFiles.length === this.fileList.length) {
        warningMsg = "确定清理所有等待上传的文件？";
      } else {
        // prettier-ignore
        warningMsg = "仅支持清理等待上传的文件，其他文件将跳过，确定清理等待上传的文件？";
      }

      this.$confirm(warningMsg, "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      })
        .then(() => {
          waitingFiles.forEach((fileInfo) => {
            // prettier-ignore
            let find = Store.uploaders.find((el) => el.schemeType === fileInfo.schemeType);
            if (find) {
              let uploadFiles = find.uploader.uploadFiles;
              let findIndex = uploadFiles.findIndex((el) =>
                this.isSameFile(el, fileInfo)
              );
              if (findIndex !== -1) {
                uploadFiles.splice(findIndex, 1);
              }
            }
          });

          this.refreshFileList();
        })
        .catch((error) => {
          console.log("An error occurred:", error);
        });
    },
    initUploadList() {
      this.isLoading = true;
      this.queryFileList().then(() => {
        this.isLoading = false;
        if (!this.timer) {
          this.timer = setInterval(this.refreshFileList, 1000);
        }
      });
    },
    deleteFileById(fileInfo) {
      if (fileInfo.status < 0) {
        let find = Store.uploaders.find(
          (el) => el.schemeType === fileInfo.schemeType
        );
        if (find) {
          let uploadFiles = find.uploader.uploadFiles;
          let findIndex = uploadFiles.findIndex((el) =>
            this.isSameFile(el, fileInfo)
          );
          if (findIndex !== -1) {
            uploadFiles.splice(findIndex, 1);
          }
        }
        return;
      }

      let fileDataId = fileInfo.id;
      deleteFileService(fileDataId).then((response) => {
        if (response.isSuccess) {
          this.$message.success("删除成功");
          this.initUploadList();
        } else {
          this.$message.error(response.errMsg);
        }
      });
    },
    uploadControl(fileInfo) {
      let find = Store.uploaders.find(
        (el) => el.schemeType === fileInfo.schemeType
      );

      let uploader = null;
      if (find) {
        uploader = find.uploader;
      } else {
        uploader = this.createUploader(fileInfo.schemeType);
      }

      let fileStatus = fileInfo.status;
      if (fileStatus === 1) {
        uploader.fire("pauseFileUpload", { fileInfo });
      }
      if (fileStatus === 2) {
        uploader.setStatus("continue");
        uploader.setCurrDataSetId(fileInfo.dataSetId);
        uploader.setCurrFileInfo(fileInfo);

        let pausedFiles = uploader.pausedFiles;
        let findPauseFile = pausedFiles.find(
          (el) => el.fileDataId === fileInfo.fileDataId
        );
        if (findPauseFile) {
          uploader.fire("continueFileUpload", { fileInfo });
        }
      }
    },
    createUploader(schemeType) {
      let find = UPLOADER_MAP.find((el) => el.schemeType === schemeType);
      let option = {
        accept: find.accept,
      };
      let uploader = new CustomUploader(schemeType, option);
      return uploader;
    },
    refreshFileList() {
      this.queryFileList().then(() => {
        let pausedFiles = this.fileList.filter((el) => el.status === 2);
        if (pausedFiles.length) {
          this.refreshDom();
        }

        const uploadStatus = [-1, -2, 0, 1, 6];
        let uploadFiles = this.fileList.filter((el) =>
          uploadStatus.includes(el.status)
        );
        if (!uploadFiles.length) {
          this.queryFileList();

          clearInterval(this.timer);
          this.timer = null;
        }
      });
    },
    queryFileList() {
      return new Promise((resolve) => {
        let params = {
          resultCount: 1000,
          skipCount: 0,
          name: "",
          status: [0, 1, 2, 6],
        };
        getFileList(params).then((response) => {
          if (response.isSuccess) {
            let result = response.data;
            let fileList = result.items;

            // 从上传列表中移除构件库上传的文件
            // prettier-ignore
            const schemeTypes = ["CLPrivateDoc", "CLPublicDoc", "Rvtuv","Capability", 'SceneAnnotationAttachment',"SceneAnchorPointAttachment"];
            fileList = fileList.filter(
              (el) => !schemeTypes.includes(el.scheme)
            );
            let uploaders = Store.uploaders.filter(
              (el) => !schemeTypes.includes(el.schemeType)
            );

            fileList.forEach((item) => {
              let bussinessInfo = item.fileBusinessInfo
                ? item.fileBusinessInfo
                : {};
              let { projName, treePathName } = bussinessInfo;
              item["fileType"] = item.suffix;
              item["schemeType"] = item.scheme;
              item["projId"] = bussinessInfo.projId;
              item["dataSetId"] = bussinessInfo.dataSetId;
              item["status"] = item.uploadStatus;
              item["length"] = item.size;
              item["uploadPathName"] = `${projName}/${treePathName}`;

              item["fileDataId"] = item.id;
              item["fileId"] = bussinessInfo.fileId;
              if (item.blobIndex || item.blobIndex === 0) {
                let rate = ((item["blobIndex"] + 1) / item.blobCount) * 10000;
                rate = Math.floor(rate);
                item["rate"] = rate;
              }
            });
            fileList = fileList.filter((el) => el.isAuthorized);

            // 前端的上传文件列表
            let customUploadList = [];
            uploaders.forEach((uploader) => {
              let uploadFiles = uploader.uploader.uploadFiles;
              let uploadingFiles = uploader.uploader.uploadingFiles;

              customUploadList = [
                ...uploadingFiles,
                ...uploadFiles,
                ...customUploadList,
              ];
            });

            customUploadList.forEach((item) => {
              let findIndex = fileList.findIndex(
                (el) =>
                  el.fileName === item.fileName &&
                  el.dataSetId === item.dataSetId
              );
              if (findIndex === -1) {
                fileList.push(item);
              } else {
                let status = fileList[findIndex].status;
                // 如果后端接口返回的状态不是上传中，则按前端状态显示
                if (status !== 1) {
                  fileList[findIndex].status = item.status;
                }
              }
            });
            this.fileList = fileList;
            resolve();
          }
        });
      });
    },
    refreshDom() {
      this.fileList.forEach((item) => {
        let find = Store.uploaders.find(
          (el) => el.schemeType === item.schemeType
        );
        let uploader = null;
        if (find) {
          uploader = find.uploader;
        } else {
          uploader = this.createUploader(item.schemeType);
        }
        let pausedFiles = uploader.pausedFiles;
        let findPauseFile = pausedFiles.find((el) => el.fileDataId === item.id);
        if (item.status === 2 && !findPauseFile) {
          let continueDom = document.getElementById(
            `dialog-continue-${item.id}`
          );
          if (continueDom) {
            if (
              !continueDom.children[0] ||
              !continueDom.children[0].className.includes("webuploader-pick")
            ) {
              uploader.uploader.addButton({
                id: `#dialog-continue-${item.id}`,
                multiple: false,
              });
            }
          }
        }
      });
    },
    destroyTimer() {
      this.timer && clearInterval(this.timer);
      this.timer = null;
    },
    isSameFile(fileInfo1, fileInfo2) {
      let isSameFileName = fileInfo1.fileName === fileInfo2.fileName;
      let isSameLength = fileInfo1.length === fileInfo2.length;

      return isSameFileName && isSameLength;
    },
    formatFileSize,
    formatTime,
  },
};
</script>

<style lang="less" scoped>
@import "@/assets/css/publishSearch.less";
.upload-table-box {
  height: 500px;

  .clear-btn-box {
    .tip-box {
      margin: 10px 0px;
      font-size: 14px;
      color: var(--page-tips-text-color);
    }
  }

  .common-table-box {
    margin-top: 15px;
    height: calc(100% - 50px);

    .common-button-box {
      display: flex;
    }
  }
}
</style>
